import { t as uiText } from '../i18n';
import React, { useEffect, useState } from 'react';
import { doc, getDoc, collection, getDocs, query, orderBy, deleteDoc, updateDoc } from 'firebase/firestore';
import { useParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../lib/firebase';
import { useAuthContext } from '../context/AuthContext';
import { useWorkspaceMembersQuery } from '../features/auth/queries/workspaceMembersQueries';
import { getCachedAvatarUrl } from '../features/auth/userImageUtils';
import { getEntitlements, formatMemberLimit } from '../features/plans/entitlements';
import InviteMemberModal from './InviteMemberModal';
import {
  approveJoinErrorMessage,
  memberLimitInfo,
  memberLimitNoticeText,
  type MemberLimitInfo,
} from '../features/workspace/memberInviteMessages';
import PlanLimitNotice from './PlanLimitNotice';
import InviteUrlModal from './InviteUrlModal';

// メンバーアバターコンポーネント
const MemberAvatar: React.FC<{ 
  memberId: string; 
  memberName: string; 
  fallbackUrl?: string; 
  size?: number 
}> = ({ 
  memberId, 
  memberName, 
  fallbackUrl,
  size = 32 
}) => {
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    const loadAvatar = async () => {
      try {
        setImgError(false);
        const url = await getCachedAvatarUrl(memberId);
        if (url) {
          setAvatarUrl(url);
        } else {
          const trimmed = (fallbackUrl || '').trim();
          setAvatarUrl(trimmed.length > 0 ? trimmed : null);
        }
      } catch (error) {
        console.error('❌ MemberAvatar読み込みエラー', { memberId, error });
        setAvatarUrl(null);
      } finally {
        setLoading(false);
      }
    };

    loadAvatar();
  }, [memberId, fallbackUrl]);

  const initial = (memberName || '?').trim().charAt(0) || '?';

  // 画像が無い（または読み込み失敗）なら、最初から最後まで文字アバターで固定
  if (loading || imgError || !avatarUrl) {
    return (
      <div
        style={{
          width: `${size}px`,
          height: `${size}px`,
          borderRadius: '50%',
          backgroundColor: '#f0f0f0',
          marginRight: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: `${size * 0.4}px`,
          color: '#666',
        }}
      >
        {initial}
      </div>
    );
  }

  return (
    <img
      src={avatarUrl}
      alt={memberName}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: '50%',
        objectFit: 'cover',
        marginRight: '12px',
      }}
      onError={(e) => {
        // 画像読み込みエラー時はデフォルトアバターを表示
        console.warn('[AvatarDebug] <img> onError', {
          memberId,
          currentSrc: e.currentTarget?.src?.substring(0, 120) || null
        });
        setImgError(true);
      }}
      onLoad={(e) => {}}
    />
  );
};

type Member = {
  id: string;
  name: string;
  photoURL?: string;
  role: 'owner' | 'admin' | 'member';
};

type Props = {
  onClose: () => void;
};

type JoinRequest = {
  userId: string;
  appliedAt: Date;
  message: string;
  userName: string;
  userPhotoURL: string;
};

const MemberListModal: React.FC<Props> = ({ onClose }) => {
  const { workspaceId: routeWorkspaceId } = useParams();
  const { user } = useAuthContext();

  const [workspacePlanId, setWorkspacePlanId] = useState<string>('free');
  const [workspaceOfferId, setWorkspaceOfferId] = useState<string | undefined>(undefined);
  const [workspaceSeriesId, setWorkspaceSeriesId] = useState<string>('');
  const [parentOffer, setParentOffer] = useState<{
    parentWorkspaceId: string;
    offerId?: string;
    planId?: string;
    seriesId?: string;
  } | null>(null);
 
  // 移行途中の互換: 旧データ(1/2)も読み取り時だけ吸収
  const normalizeRole = (role: unknown): 'owner' | 'admin' | 'member' => {
    if (role === 'owner') return 'owner';
    if (role === 'admin') return 'admin';
    if (role === 'member') return 'member';
    // 旧データフォールバック: 1=管理者, 2=メンバー
    if (role === 1) return 'admin';
    if (role === 2) return 'member';
    return 'member';
  };
  
  // React Queryを使用してメンバー一覧を取得
  const { data: cachedMembers } = useWorkspaceMembersQuery(
    routeWorkspaceId ?? '',
    !!routeWorkspaceId
  );
  
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isInviteUrlOpen, setIsInviteUrlOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'members' | 'joinRequests'>('members');
  const [joinRequests, setJoinRequests] = useState<JoinRequest[]>([]);
  const [loadingJoinRequests, setLoadingJoinRequests] = useState(false);
  const [approveLimitInfo, setApproveLimitInfo] = useState<MemberLimitInfo | null>(null);
  const [menuOpenMemberId, setMenuOpenMemberId] = useState<string | null>(null);

  // 役割変更
  const handleChangeRole = async (targetUserId: string, newRole: 'owner' | 'admin' | 'member') => {
    if (!routeWorkspaceId) return;
    if (newRole === 'owner') {
      if (!isOwner) return;
    } else {
      if (!isAdmin) return;
    }
    try {
      const currentRole = members.find((m) => m.id === targetUserId)?.role;
      if (currentRole === newRole) return;

      const memberRef = doc(db, `workspaces/${routeWorkspaceId}/members/${targetUserId}`);
      await updateDoc(memberRef, { role: newRole });

      // ローカル状態を反映
      setMembers((prev) => prev.map((m) => (m.id === targetUserId ? { ...m, role: newRole } : m)));
    } catch (error) {
      console.error('役割の更新に失敗しました:', error);
    } finally {
      setMenuOpenMemberId(null);
    }
  };

  // メンバーから外す
  const handleRemoveMember = async (targetUserId: string, targetUserName: string) => {
    if (!routeWorkspaceId || !isAdmin) return;

    const confirmed = window.confirm(uiText("Remove {{targetUserName}} from this group?", { targetUserName: targetUserName }));
    if (!confirmed) return;

    try {
      // workspaces/{workspaceId}/members/{uid} を削除
      const memberRef = doc(db, `workspaces/${routeWorkspaceId}/members/${targetUserId}`);
      await deleteDoc(memberRef);

      // users/{uid}/workspaces/{workspaceId} を削除
      const userWorkspaceRef = doc(db, `users/${targetUserId}/workspaces/${routeWorkspaceId}`);
      await deleteDoc(userWorkspaceRef);

      // ローカル状態を更新
      setMembers((prev) => prev.filter((m) => m.id !== targetUserId));
    } catch (error) {
      console.error('メンバーから外す処理に失敗しました:', error);
    } finally {
      setMenuOpenMemberId(null);
    }
  };

  // 承認処理
  const handleApprove = async (userId: string) => {
    if (!routeWorkspaceId) return;
    setApproveLimitInfo(null);
    
    try {
      // メンバー追加・所属一覧への追加・申請削除はサーバー側（人数上限の検証つき）で行う
      const approveJoinRequest = httpsCallable(functions, 'approveJoinRequestFn');
      await approveJoinRequest({ workspaceId: routeWorkspaceId, userId });

      // ローカル状態を更新
      setJoinRequests(prev => prev.filter(request => request.userId !== userId));
      setMembers(prev => [...prev, {
        id: userId,
        name: joinRequests.find(r => r.userId === userId)?.userName || 'Unknown User',
        photoURL: joinRequests.find(r => r.userId === userId)?.userPhotoURL || '',
        role: 'member',
      }]);
    } catch (error: unknown) {
      console.error('承認処理に失敗:', error);
      const info = memberLimitInfo(error);
      if (info) {
        setApproveLimitInfo(info);
        return;
      }
      window.alert(approveJoinErrorMessage(error));
    }
  };

  // 取消処理
  const handleReject = async (userId: string) => {
    if (!routeWorkspaceId) return;
    
    try {
      // joinRequestsサブコレクションから該当の申請データを削除
      const joinRequestRef = doc(db, `workspaces/${routeWorkspaceId}/joinRequests/${userId}`);
      await deleteDoc(joinRequestRef);

      // ローカル状態からも削除
      setJoinRequests(prev => prev.filter(request => request.userId !== userId));
    } catch (error) {
      console.error('申請の取消に失敗:', error);
    }
  };

  useEffect(() => {
    const fetchWorkspacePlan = async () => {
      if (!routeWorkspaceId) {
        setWorkspacePlanId('free');
        setWorkspaceOfferId(undefined);
        setWorkspaceSeriesId('');
        setParentOffer(null);
        return;
      }
      try {
        const snap = await getDoc(doc(db, 'workspaces', routeWorkspaceId));
        if (!snap.exists()) {
          setWorkspacePlanId('free');
          setWorkspaceOfferId(undefined);
          setWorkspaceSeriesId('');
          setParentOffer(null);
          return;
        }
        const data = snap.data() as any;
        const raw = typeof data?.planId === 'string' ? data.planId.trim() : '';
        const offerId = typeof data?.offerId === 'string' ? data.offerId : undefined;
        const seriesId = typeof data?.seriesId === 'string' ? data.seriesId.trim() : '';
        const parentId = typeof data?.parentWorkspaceId === 'string' ? data.parentWorkspaceId.trim() : '';
        setWorkspacePlanId(raw || 'free');
        setWorkspaceOfferId(offerId);
        setWorkspaceSeriesId(seriesId);
        if (parentId) {
          try {
            const parentSnap = await getDoc(doc(db, 'workspaces', parentId));
            const p = parentSnap.data() as any;
            setParentOffer({
              parentWorkspaceId: parentId,
              offerId: typeof p?.offerId === 'string' ? p.offerId : undefined,
              planId: typeof p?.planId === 'string' ? p.planId : undefined,
              seriesId: typeof p?.seriesId === 'string' ? p.seriesId : undefined,
            });
          } catch {
            setParentOffer({ parentWorkspaceId: parentId });
          }
        } else {
          setParentOffer(null);
        }
      } catch (e) {
        console.error('[MemberListModal] Failed to load workspace planId:', e);
        setWorkspacePlanId('free');
        setWorkspaceOfferId(undefined);
        setWorkspaceSeriesId('');
        setParentOffer(null);
      }
    };

    void fetchWorkspacePlan();
  }, [routeWorkspaceId]);

  useEffect(() => {
    const fetchMembers = async () => {
      if (!routeWorkspaceId || !user) return;
      setLoading(true);

      try {
        // 1. キャッシュから取得を試行
        // React Queryから取得したデータを使用
        const membersData = cachedMembers;

        if (membersData && membersData.size > 0) {
          const memberData: Member[] = [];
          let currentUserRole: 'owner' | 'admin' | 'member' = 'member'; // デフォルトはメンバー

          for (const [uid, memberInfo] of Array.from(membersData.entries())) {
            // ユーザー情報は別途キャッシュから取得（userUtilsのキャッシュを活用）
            const { getUserInfo } = await import('../utils/userUtils');
            const userInfo = await getUserInfo(uid);
            
            if (userInfo) {
              const role = normalizeRole((memberInfo as any)?.role);
              const member = {
                id: uid,
                name: userInfo.displayName || 'No Name',
                photoURL: userInfo.photoURL || '',
                role,
              };
              memberData.push(member);
              
              if (uid === user.uid) {
                currentUserRole = role;
              }
            }
          }

          setMembers(memberData);
          setIsAdmin(currentUserRole === 'owner' || currentUserRole === 'admin');
          setIsOwner(currentUserRole === 'owner');
          setLoading(false);
          return;
        }

        // 2. キャッシュがない場合はFirestoreから取得
        const membersColRef = collection(db, `workspaces/${routeWorkspaceId}/members`);
        const membersSnapshot = await getDocs(membersColRef);

        const memberData: Member[] = [];
        let currentUserRole: 'owner' | 'admin' | 'member' = 'member'; // デフォルトはメンバー

        for (const memberDoc of membersSnapshot.docs) {
          const uid = memberDoc.id;
          const role = normalizeRole(memberDoc.data().role);

          const userRef = doc(db, 'users', uid);
          const userSnap = await getDoc(userRef);

          if (userSnap.exists()) {
            const userData = userSnap.data();

            const member = {
              id: uid,
              name: userData.displayName || 'No Name',
              photoURL: userData.photoURL || '',
              role,
            };
            memberData.push(member);

            if (uid === user.uid) {
              currentUserRole = role;
            }
          } else {
            console.error(`   - users/${uid} が見つかりません！`);
          }
        }

        setMembers(memberData);
        setIsAdmin(currentUserRole === 'owner' || currentUserRole === 'admin');
        setIsOwner(currentUserRole === 'owner');
      } catch (error) {
        console.error('メンバーの取得に失敗しました:', error);
      } finally {
        setLoading(false);
      }
    };

    // 参加申請データを取得
    const fetchJoinRequests = async () => {
      if (!routeWorkspaceId) return;
      
      setLoadingJoinRequests(true);
      try {
        const joinRequestsRef = collection(db, `workspaces/${routeWorkspaceId}/joinRequests`);
        const q = query(joinRequestsRef, orderBy('appliedAt', 'desc'));
        const snapshot = await getDocs(q);
        
        const requests: JoinRequest[] = [];
        
        // 各申請のユーザー情報を個別取得
        for (const requestDoc of snapshot.docs) {
          const data = requestDoc.data();
          const uid = data.userId;
          
          // ユーザー情報をキャッシュから取得
          const { getUserInfo } = await import('../utils/userUtils');
          const userInfo = await getUserInfo(uid);
          
          if (userInfo) {
            requests.push({
              userId: uid,
              appliedAt: data.appliedAt?.toDate() || new Date(),
              message: data.message || '',
              userName: userInfo.displayName || 'No Name',
              userPhotoURL: userInfo.photoURL || '',
            });
          } else {
            console.error(`参加申請のユーザー ${uid} が見つかりません！`);
            // ユーザーが見つからない場合でも申請は表示
            requests.push({
              userId: uid,
              appliedAt: data.appliedAt?.toDate() || new Date(),
              message: data.message || '',
              userName: 'Unknown User',
              userPhotoURL: '',
            });
          }
        }
        
        setJoinRequests(requests);
      } catch (error) {
        console.error('参加申請の取得に失敗:', error);
      } finally {
        setLoadingJoinRequests(false);
      }
    };

    fetchMembers();
    if (routeWorkspaceId) {
      fetchJoinRequests();
    }
  }, [cachedMembers, routeWorkspaceId, user]);

  const getRoleLabel = (role: 'owner' | 'admin' | 'member') => {
    switch (role) {
      case 'owner':
        return uiText("Owner");
      case 'admin':
        return uiText("Administrator");
      default:
        return uiText("Members");
    }
  };

  const memberCount = members.length;
  const entitlements = getEntitlements({
    offerId: workspaceOfferId,
    planId: workspacePlanId,
    seriesId: workspaceSeriesId,
    parentWorkspaceId: parentOffer?.parentWorkspaceId,
    parent: parentOffer
      ? { offerId: parentOffer.offerId, planId: parentOffer.planId, seriesId: parentOffer.seriesId }
      : undefined,
  });
  const memberLimitLabel = formatMemberLimit(entitlements.memberLimit);

  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            width: '640px',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              padding: '16px',
              borderBottom: '1px solid #ddd',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontWeight: 'bold',
              fontSize: '18px',
            }}
          >
            {uiText("Member management")}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ fontSize: '12px', color: '#666', fontWeight: 600 }}>
                {uiText("Current plan:")}<span style={{ color: '#333' }}>
                  {parentOffer
                    ? uiText("{{v0}} (inherits parent plan)", { v0: (workspacePlanId === 'child' ? (parentOffer.planId || 'free') : workspacePlanId) || 'free' })
                    : (workspacePlanId || 'free')}
                </span>
              </div>
              <button
                onClick={onClose}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '18px',
                  cursor: 'pointer',
                }}
              >
                ×
              </button>
            </div>
          </div>

          {loading ? (
            <div style={{ padding: '16px' }}>{uiText("Loading now...")}</div>
          ) : (
            <>
              {isAdmin && (
                <div
                  style={{
                    padding: '16px',
                    borderBottom: '1px solid #eee',
                    cursor: 'pointer',
                  }}
                  onClick={() => setIsInviteOpen(true)}
                >
                  <div
                    style={{
                      color: 'var(--workspace-primary, #cfae91)',
                      fontWeight: 'bold',
                      marginBottom: '8px',
                    }}
                  >
                    {uiText("+ Add a member directly")}
                  </div>
                  <div
                    style={{
                      fontSize: '8pt',
                      color: '#666',
                      lineHeight: '1.4',
                    }}
                  >
                    {uiText("Add a member directly by specifying their notreu account email address")}<br />
                    {uiText("※ The person you add must already have a notreu account")}
                  </div>
                </div>
              )}

              {isAdmin && (
                <div
                  style={{
                    padding: '16px',
                    borderBottom: '1px solid #eee',
                    cursor: 'pointer',
                  }}
                  onClick={() => setIsInviteUrlOpen(true)}
                >
                  <div
                    style={{
                      color: 'var(--workspace-primary, #cfae91)',
                      fontWeight: 'bold',
                      marginBottom: '8px',
                    }}
                  >
                    {uiText("+ Create a member invite URL")}
                  </div>
                  <div
                    style={{
                      fontSize: '8pt',
                      color: '#666',
                      lineHeight: '1.4',
                    }}
                  >
                    {uiText("This URL is for requesting to join the team as a member")}<br />
                    {uiText("You can set an expiration and require approval before someone can join")}
                  </div>
                </div>
              )}

              {/* タブナビゲーション */}
              <div
                style={{
                  display: 'flex',
                  borderBottom: '1px solid #eee',
                }}
              >
                <button
                  onClick={() => setActiveTab('members')}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    border: 'none',
                    backgroundColor: activeTab === 'members' ? 'var(--workspace-primary, #cfae91)' : '#f8f9fa',
                    color: activeTab === 'members' ? '#fff' : '#666',
                    fontWeight: activeTab === 'members' ? 'bold' : 'normal',
                    cursor: 'pointer',
                    fontSize: '14px',
                    borderBottom: activeTab === 'members' ? '2px solid var(--workspace-primary, #cfae91)' : 'none',
                  }}
                >
                  {uiText("Member list")}
                </button>
                {isAdmin && (
                  <button
                    onClick={() => setActiveTab('joinRequests')}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: activeTab === 'joinRequests' ? 'var(--workspace-primary, #cfae91)' : '#f8f9fa',
                      color: activeTab === 'joinRequests' ? '#fff' : '#666',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: activeTab === 'joinRequests' ? 'bold' : 'normal',
                      borderBottom: activeTab === 'joinRequests' ? '2px solid var(--workspace-primary, #cfae91)' : 'none',
                    }}
                  >
                    {uiText("Join requests")}
                  </button>
                )}
              </div>

              {/* タブコンテンツ */}
              {activeTab === 'members' && (
                <>
                  <ul style={{ listStyle: 'none', padding: '0', margin: '0' }}>
                    {members.map((member) => (
                      <li
                        key={member.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px 16px',
                          borderBottom: '1px solid #eee',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <MemberAvatar 
                            memberId={member.id} 
                            memberName={member.name} 
                            fallbackUrl={member.photoURL}
                            size={32}
                          />
                          <div>{member.name}</div>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            fontSize: '12px',
                            color: '#666',
                            position: 'relative',
                          }}
                        >
                          <span>{getRoleLabel(member.role)}</span>
                          <div style={{ width: '26px', height: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {isAdmin && member.id !== user?.uid && (
                              <button
                                aria-label={uiText("Member settings")}
                                title={uiText("Settings")}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: '18px',
                                  lineHeight: 1,
                                  padding: '4px',
                                  color: '#888',
                                }}
                                onClick={() => setMenuOpenMemberId((prev) => (prev === member.id ? null : member.id))}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.color = '#555';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.color = '#888';
                                }}
                              >
                                ⋮
                              </button>
                            )}
                          </div>

                          {isAdmin && menuOpenMemberId === member.id && (
                            <div
                              style={{
                                position: 'absolute',
                                bottom: '28px',
                                right: 0,
                                backgroundColor: '#fff',
                                border: '1px solid #eee',
                                borderRadius: '6px',
                                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.12)',
                                zIndex: 10,
                                minWidth: '150px',
                                overflow: 'hidden',
                              }}
                            >
                            {isOwner ? (
                              <>
                                <button
                                  onClick={() => handleChangeRole(member.id, 'owner')}
                                  disabled={member.role === 'owner'}
                                  style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    background: 'none',
                                    border: 'none',
                                    textAlign: 'left',
                                    fontSize: '13px',
                                    color: member.role === 'owner' ? '#999' : '#333',
                                    cursor: member.role === 'owner' ? 'not-allowed' : 'pointer',
                                    opacity: member.role === 'owner' ? 0.6 : 1,
                                  }}
                                  onMouseEnter={(e) => {
                                    if (member.role !== 'owner') e.currentTarget.style.backgroundColor = '#f5f2ed';
                                  }}
                                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                >
                                  {uiText("Make owner")}
                                </button>
                                <button
                                  onClick={() => handleChangeRole(member.id, 'admin')}
                                  disabled={member.role === 'admin'}
                                  style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    background: 'none',
                                    borderTop: '1px solid #f0f0f0',
                                    borderLeft: 'none',
                                    borderRight: 'none',
                                    borderBottom: 'none',
                                    textAlign: 'left',
                                    fontSize: '13px',
                                    color: member.role === 'admin' ? '#999' : '#333',
                                    cursor: member.role === 'admin' ? 'not-allowed' : 'pointer',
                                    opacity: member.role === 'admin' ? 0.6 : 1,
                                  }}
                                  onMouseEnter={(e) => {
                                    if (member.role !== 'admin') e.currentTarget.style.backgroundColor = '#f5f2ed';
                                  }}
                                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                >
                                  {uiText("Make administrator")}
                                </button>
                                <button
                                  onClick={() => handleChangeRole(member.id, 'member')}
                                  disabled={member.role === 'member'}
                                  style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    background: 'none',
                                    borderTop: '1px solid #f0f0f0',
                                    borderLeft: 'none',
                                    borderRight: 'none',
                                    borderBottom: 'none',
                                    textAlign: 'left',
                                    fontSize: '13px',
                                    color: member.role === 'member' ? '#999' : '#333',
                                    cursor: member.role === 'member' ? 'not-allowed' : 'pointer',
                                    opacity: member.role === 'member' ? 0.6 : 1,
                                  }}
                                  onMouseEnter={(e) => {
                                    if (member.role !== 'member') e.currentTarget.style.backgroundColor = '#f5f2ed';
                                  }}
                                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                >
                                  {uiText("Make member")}
                                </button>
                                <button
                                  onClick={() => handleRemoveMember(member.id, member.name)}
                                  style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    background: 'none',
                                    borderTop: '1px solid #f0f0f0',
                                    borderLeft: 'none',
                                    borderRight: 'none',
                                    borderBottom: 'none',
                                    textAlign: 'left',
                                    fontSize: '13px',
                                    color: '#c0392b',
                                    cursor: 'pointer',
                                  }}
                                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#fff5f5')}
                                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                >
                                  {uiText("Remove from this group")}
                                </button>
                              </>
                            ) : member.role === 'admin' ? (
                              <>
                                <button
                                  onClick={() => handleChangeRole(member.id, 'member')}
                                  style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    background: 'none',
                                    border: 'none',
                                    textAlign: 'left',
                                    fontSize: '13px',
                                    color: '#333',
                                    cursor: 'pointer',
                                  }}
                                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f2ed')}
                                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                >
                                  {uiText("Make member")}
                                </button>
                                <button
                                  onClick={() => handleRemoveMember(member.id, member.name)}
                                  style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    background: 'none',
                                    borderTop: '1px solid #f0f0f0',
                                    borderLeft: 'none',
                                    borderRight: 'none',
                                    borderBottom: 'none',
                                    textAlign: 'left',
                                    fontSize: '13px',
                                    color: '#c0392b',
                                    cursor: 'pointer',
                                  }}
                                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#fff5f5')}
                                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                >
                                  {uiText("Remove from this group")}
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleChangeRole(member.id, 'admin')}
                                  style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    background: 'none',
                                    border: 'none',
                                    textAlign: 'left',
                                    fontSize: '13px',
                                    color: '#333',
                                    cursor: 'pointer',
                                  }}
                                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f2ed')}
                                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                >
                                  {uiText("Make administrator")}
                                </button>
                                <button
                                  onClick={() => handleRemoveMember(member.id, member.name)}
                                  style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    background: 'none',
                                    borderTop: '1px solid #f0f0f0',
                                    borderLeft: 'none',
                                    borderRight: 'none',
                                    borderBottom: 'none',
                                    textAlign: 'left',
                                    fontSize: '13px',
                                    color: '#c0392b',
                                    cursor: 'pointer',
                                  }}
                                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#fff5f5')}
                                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                >
                                  {uiText("Remove from this group")}
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                  </ul>

                  {/* メンバー数と上限 */}
                  <div
                    style={{
                      padding: '12px 16px',
                      borderTop: '1px solid #eee',
                      backgroundColor: '#fafafa',
                      fontSize: '12px',
                      color: '#666',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div>
                      {uiText("Member count:")} <strong style={{ color: '#333' }}>{memberCount}{uiText(" people")}</strong>
                    </div>
                    <div>
                      {uiText("Limit:")} <strong style={{ color: '#333' }}>{memberLimitLabel}</strong>
                    </div>
                  </div>
                </>
              )}

              {isAdmin && activeTab === 'joinRequests' && (
                <div>
                  {approveLimitInfo && (
                    <div style={{ padding: '0 16px 12px' }}>
                      <PlanLimitNotice
                        kind="members"
                        isAdmin={isAdmin}
                        {...memberLimitNoticeText(approveLimitInfo)}
                        extraActions={
                          <button
                            type="button"
                            onClick={() => {
                              setApproveLimitInfo(null);
                              setActiveTab('members');
                            }}
                            style={{
                              padding: '8px 14px',
                              borderRadius: 6,
                              border: '1px solid var(--workspace-primary, #cfae91)',
                              background: '#fff',
                              color: 'var(--workspace-primary, #cfae91)',
                              fontWeight: 700,
                              cursor: 'pointer',
                            }}
                          >
                            {uiText('Manage members')}
                          </button>
                        }
                      />
                    </div>
                  )}
                  {loadingJoinRequests ? (
                    <div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
                      {uiText("Loading now...")}
                    </div>
                  ) : joinRequests.length === 0 ? (
                    <div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
                      {uiText("No join requests")}
                    </div>
                  ) : (
                    <ul style={{ listStyle: 'none', padding: '0', margin: '0' }}>
                      {joinRequests.map((request, index) => (
                        <li
                          key={request.userId}
                          style={{
                            padding: '16px',
                            borderBottom: index < joinRequests.length - 1 ? '1px solid #eee' : 'none',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                            <MemberAvatar
                              memberId={request.userId}
                              memberName={request.userName}
                              fallbackUrl={request.userPhotoURL}
                              size={32}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '14px', color: '#333', fontWeight: 'bold' }}>
                                {request.userName}
                              </div>
                              <div style={{ fontSize: '12px', color: '#666' }}>
                                {uiText("Applied at:")} {request.appliedAt.toLocaleString('ja-JP', { 
                                  timeZone: 'Asia/Tokyo',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  second: '2-digit'
                                })}
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                onClick={() => handleApprove(request.userId)}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: 'var(--workspace-primary, #cfae91)',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                  fontWeight: 'bold'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = 'var(--workspace-primary-dark, #b89a7a)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'var(--workspace-primary, #cfae91)';
                                }}
                              >
                                {uiText("Approve")}
                              </button>
                              <button
                                onClick={() => handleReject(request.userId)}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: '#e74c3c',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                  fontWeight: 'bold'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#c0392b';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = '#e74c3c';
                                }}
                              >
                                {uiText("Withdraw")}
                              </button>
                            </div>
                          </div>
                          {request.message && (
                            <div style={{ 
                              fontSize: '14px', 
                              color: '#333', 
                              backgroundColor: '#f8f9fa',
                              padding: '8px 12px',
                              borderRadius: '4px',
                              border: '1px solid #eee',
                              marginLeft: '44px' // プロフィール画像の幅 + マージン
                            }}>
                              {uiText("Message:")} {request.message}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {isInviteOpen && <InviteMemberModal onClose={() => setIsInviteOpen(false)} />}
      {isInviteUrlOpen && <InviteUrlModal onClose={() => setIsInviteUrlOpen(false)} />}
    </>
  );
};

export default MemberListModal;
